import type { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import type { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Material } from '@babylonjs/core/Materials/material';
import type { Geometry } from '@babylonjs/core/Meshes/geometry';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import type { AbstractScene } from '@babylonjs/core/abstractScene';
import type { Node } from '@babylonjs/core/node';
import type { Scene } from '@babylonjs/core/scene';
import type { Nullable } from '@babylonjs/core/types';
import type { ITransformRT2 } from '@seddi/math-v2';
import { FabricSide, type IGarmentPieceGrain, type IGarmentPieceLogo } from '@seddi/scene';
import {
  type VertexDataDesc,
  geometryCreate,
  materialSetTexturesTransform,
  textureSetSize,
} from '@seddi/ui-babylon-core';
import type { IBabylonFabric } from '../resources/fabricTypes';
import { LogoMaterial } from '../resources/logoMaterial';
import type { IBabylonLogo } from '../resources/logoTypes';

export interface LogoRenderable {
  material: PBRMaterial;
  mesh: Mesh;
}

interface Side {
  material: PBRMaterial;
  mesh: Mesh;
}

export class PieceRenderable {
  /** Piece ID. */
  public readonly id: string;
  /** Babylon scene. */
  public readonly scene: Scene;
  /** Parent node. */
  public readonly parent: Nullable<Node>;

  /** Back renderable. */
  readonly bk: Side;
  /** Front renderable. */
  readonly fr: Side;
  /** Logos renderables.  */
  readonly logos: Map<string, LogoRenderable>;

  /** Mesh geometry. */
  private _geometry!: Geometry;

  _parentContainer?: Nullable<AbstractScene> = null;

  constructor(id: string, scene: Scene, parent?: Nullable<Node>) {
    this.id = id;
    this.scene = scene;
    this.parent = parent!;

    this._parentContainer = parent?._parentContainer;
    // create meshes
    this.scene._blockEntityCollection = Boolean(this._parentContainer);
    const bkMesh = new Mesh(`${id}.mesh.bk`, this.scene);
    const frMesh = new Mesh(`${id}.mesh.fr`, this.scene);
    this.scene._blockEntityCollection = false;
    bkMesh.overrideMaterialSideOrientation = Material.ClockWiseSideOrientation;
    bkMesh.parent = parent!;
    frMesh.overrideMaterialSideOrientation = Material.CounterClockWiseSideOrientation;
    frMesh.parent = parent!;
    this.bk = { mesh: bkMesh } as Side;
    this.fr = { mesh: frMesh } as Side;
    this.logos = new Map();
  }

  get geometry(): Geometry {
    return this._geometry;
  }

  setFabric(fabric: IBabylonFabric, grain: IGarmentPieceGrain) {
    const frMaterial = this.createSide(fabric.front, `${this.id}-F`, grain, FabricSide.FRONT);
    this.fr.material = frMaterial;
    this.fr.mesh.material = frMaterial;
    const bkMaterial = fabric.back
      ? this.createSide(fabric.back, `${this.id}-B`, grain, FabricSide.BACK)
      : frMaterial;
    this.bk.material = bkMaterial;
    this.bk.mesh.material = bkMaterial;
  }

  /**
   * Recreate geometry and apply to piece meshes.
   * @param geometry - Geometry descriptor.
   */
  setGeometry(geometry: VertexDataDesc) {
    this.scene._blockEntityCollection = Boolean(this._parentContainer);
    this._geometry = geometryCreate(this.id, this.scene, geometry, true);
    this._geometry._parentContainer = this._parentContainer!;
    this.scene._blockEntityCollection = false;
    this._geometry.applyToMesh(this.fr.mesh);
    this._geometry.applyToMesh(this.bk.mesh);
  }

  /**
   * Create piece side material.
   * @param template - Base material.
   * @param name - Name.
   * @param grain - Piece grain.
   * @param side - Piece side.
   */
  private createSide(
    template: PBRMaterial,
    name: string,
    grain: IGarmentPieceGrain,
    side: FabricSide,
  ) {
    const material = template.clone(name);
    const transform: ITransformRT2 = { translation: grain.origin, rotation: grain.orientation };
    materialSetTexturesTransform(material, transform);
    return material;
  }

  /**
   * Create piece logo.
   * @param pieceLogo - Piece logo data.
   * @param logo - Logo resource.
   */
  createLogo(pieceLogo: IGarmentPieceLogo, logo: IBabylonLogo) {
    // Clone and configure material.
    const material = LogoMaterial.clone(logo.material, pieceLogo._id);
    material.zOffset = -1.1;

    const { size, transform } = pieceLogo;
    const albedoTexture = material.albedoTexture as Texture;
    albedoTexture.homogeneousRotationInUVTransform = true;
    albedoTexture.uOffset = 0.5 - transform.translation.x * (1.0 / size.x);
    albedoTexture.vOffset = 0.5 - transform.translation.y * (1.0 / size.y);
    albedoTexture.uRotationCenter = transform.translation.x;
    albedoTexture.vRotationCenter = transform.translation.y;
    albedoTexture.wAng = -transform.rotation;
    textureSetSize(albedoTexture, size);

    // Create mesh with piece geometry.
    this.scene._blockEntityCollection = Boolean(this._parentContainer);
    const mesh = new Mesh(pieceLogo._id, this.scene);
    this.scene._blockEntityCollection = false;
    mesh.material = material;
    mesh.overrideMaterialSideOrientation = Material.CounterClockWiseSideOrientation;
    mesh.parent = this.parent;
    this._geometry.applyToMesh(mesh);

    // Save logo renderable.
    const logoRenderable: LogoRenderable = { material: logo.material, mesh };
    this.logos.set(pieceLogo._id, logoRenderable);

    return logoRenderable;
  }
}
